import { useEffect, useMemo, useState } from "react";
import type { UiSnapshotV2 } from "@/ui/runtime/contracts/manager-ui-v2";
import { Badge } from "@/ui/foundation/primitives/badge";
import { Button } from "@/ui/foundation/primitives/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui/foundation/primitives/card";
import { DataList } from "@/ui/components/common/data-list";
import { KeyValueRow } from "@/ui/components/common/info-display";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui/foundation/primitives/table";
import { cn } from "@/ui/foundation/lib/utils";
import { ManagerSecondaryPane } from "@/ui/components/manager/manager-layout";
import { fallbackFixtureMonth, formatDateLabel, type FixtureMonth } from "@/ui/components/manager/manager-ui-helpers";

interface UseFixturesScreenStateResult {
    fixtureMonths: FixtureMonth[];
    selectedMonth: FixtureMonth;
    selectedFixture: FixtureMonth["matches"][number] | null;
    fixturesTodayMonthId: string | null;
    selectMonth(monthId: string): void;
    selectFixture(fixtureId: string): void;
}

export function useFixturesScreenState(snapshot: UiSnapshotV2): UseFixturesScreenStateResult {
    const [selectedMonthId, setSelectedMonthId] = useState<string | null>(null);
    const [selectedFixtureId, setSelectedFixtureId] = useState<string | null>(null);

    const fixtureMonths = useMemo<FixtureMonth[]>(() => {
        const byMonth = new Map<string, FixtureMonth>();
        for (const fixture of snapshot.routes.fixtures.fixtures) {
            const dateLabel = formatDateLabel(fixture.date);
            const monthId = fixture.date.slice(0, 7);
            const monthLabel = new Intl.DateTimeFormat(undefined, { month: "short", year: "numeric" }).format(new Date(`${monthId}-01T12:00:00.000Z`));
            const existing = byMonth.get(monthId) ?? { id: monthId, label: monthLabel, matches: [] };
            existing.matches.push({
                id: `${fixture.date}-${fixture.home}-${fixture.away}`,
                dateIso: fixture.date,
                dateLabel,
                home: fixture.home,
                away: fixture.away,
                competition: "League",
                result: fixture.status === "completed" ? `${fixture.homeScore ?? 0}-${fixture.awayScore ?? 0}` : "-",
                isManagedFixture: fixture.home === snapshot.teamName || fixture.away === snapshot.teamName,
            });
            byMonth.set(monthId, existing);
        }
        return Array.from(byMonth.values());
    }, [snapshot.routes.fixtures.fixtures, snapshot.teamName]);

    useEffect(() => {
        if (!selectedMonthId && fixtureMonths[0]) {
            setSelectedMonthId(fixtureMonths[0].id);
        }
    }, [selectedMonthId, fixtureMonths]);

    const selectedMonth = useMemo(
        () => fixtureMonths.find((month) => month.id === selectedMonthId) ?? fixtureMonths[0] ?? fallbackFixtureMonth,
        [fixtureMonths, selectedMonthId]
    );

    useEffect(() => {
        if ((!selectedFixtureId || !selectedMonth.matches.some((match) => match.id === selectedFixtureId)) && selectedMonth.matches[0]) {
            setSelectedFixtureId(selectedMonth.matches[0].id);
        }
    }, [selectedFixtureId, selectedMonth.matches]);

    const selectedFixture = useMemo(
        () => selectedMonth.matches.find((match) => match.id === selectedFixtureId) ?? selectedMonth.matches[0] ?? null,
        [selectedFixtureId, selectedMonth.matches]
    );

    const fixturesTodayMonthId = useMemo(() => {
        const managedUpcoming = snapshot.routes.fixtures.fixtures.find(
            (fixture) => fixture.status === "scheduled" && (fixture.home === snapshot.teamName || fixture.away === snapshot.teamName)
        );
        const fallbackUpcoming = snapshot.routes.fixtures.fixtures.find((fixture) => fixture.status === "scheduled");
        const chosen = managedUpcoming ?? fallbackUpcoming ?? snapshot.routes.fixtures.fixtures[0];
        return chosen?.date.slice(0, 7) ?? null;
    }, [snapshot.routes.fixtures.fixtures, snapshot.teamName]);

    return {
        fixtureMonths,
        selectedMonth,
        selectedFixture,
        fixturesTodayMonthId,
        selectMonth: setSelectedMonthId,
        selectFixture: setSelectedFixtureId,
    };
}

interface FixturesScreenSecondaryProps {
    state: UseFixturesScreenStateResult;
}

export function FixturesScreenSecondary({ state }: FixturesScreenSecondaryProps) {
    return (
        <ManagerSecondaryPane
            title="Fixtures"
            headerAction={(
                <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={() => {
                        if (state.fixturesTodayMonthId) {
                            state.selectMonth(state.fixturesTodayMonthId);
                        }
                    }}
                >
                    Today
                </Button>
            )}
        >
            <DataList
                items={state.fixtureMonths}
                getKey={(month) => month.id}
                selectedKey={state.selectedMonth.id}
                onSelect={(month) => state.selectMonth(month.id)}
                itemClassName="flex items-center justify-between"
                renderItem={(month) => (
                    <>
                        <span className="text-sm font-medium">{month.label}</span>
                        <span className="text-xs text-muted-foreground">{month.matches.length}</span>
                    </>
                )}
            />
        </ManagerSecondaryPane>
    );
}

interface FixturesScreenMainProps {
    snapshot: UiSnapshotV2;
    state: UseFixturesScreenStateResult;
}

export function FixturesScreenMain({ snapshot, state }: FixturesScreenMainProps) {
    return (
        <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
            <Card>
                <CardHeader>
                    <CardDescription>Month</CardDescription>
                    <CardTitle>{state.selectedMonth.label}</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {Object.entries(
                            state.selectedMonth.matches.reduce<Record<string, typeof state.selectedMonth.matches>>((grouped, match) => {
                                const key = match.dateLabel;
                                if (!grouped[key]) {
                                    grouped[key] = [];
                                }
                                grouped[key].push(match);
                                return grouped;
                            }, {})
                        ).map(([dateLabel, matches]) => (
                            <div key={`${state.selectedMonth.id}-${dateLabel}`} className="rounded-md border">
                                <div className="border-b bg-muted/30 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                    {dateLabel}
                                </div>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Home</TableHead>
                                            <TableHead>Away</TableHead>
                                            <TableHead>Comp</TableHead>
                                            <TableHead className="text-right">Result</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {matches.map((match) => (
                                            <TableRow
                                                key={match.id}
                                                className={cn(
                                                    "cursor-pointer",
                                                    match.id === state.selectedFixture?.id ? "bg-muted/60" : "",
                                                    match.isManagedFixture ? "bg-primary/5" : ""
                                                )}
                                                onClick={() => state.selectFixture(match.id)}
                                            >
                                                <TableCell className={cn(match.home === snapshot.teamName ? "font-semibold text-primary" : "")}>{match.home}</TableCell>
                                                <TableCell className={cn(match.away === snapshot.teamName ? "font-semibold text-primary" : "")}>{match.away}</TableCell>
                                                <TableCell>{match.competition}</TableCell>
                                                <TableCell className="text-right">{match.result}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
            <Card className="lg:sticky lg:top-4 lg:self-start">
                <CardHeader>
                    <CardTitle>{state.selectedFixture ? `${state.selectedFixture.home} vs ${state.selectedFixture.away}` : "Match Details"}</CardTitle>
                    <CardDescription>{state.selectedFixture?.dateLabel ?? "Select a fixture"}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                    {state.selectedFixture ? (
                        <>
                            <KeyValueRow label="Competition" value={<Badge variant="secondary">{state.selectedFixture.competition}</Badge>} />
                            <KeyValueRow
                                label="Managed Team Involved"
                                value={(
                                    <Badge variant={state.selectedFixture.isManagedFixture ? "default" : "outline"}>
                                        {state.selectedFixture.isManagedFixture ? "Yes" : "No"}
                                    </Badge>
                                )}
                            />
                            <KeyValueRow
                                label="Status"
                                value={(
                                    <Badge variant={state.selectedFixture.result === "-" ? "outline" : "secondary"}>
                                        {state.selectedFixture.result === "-" ? "Scheduled" : "Completed"}
                                    </Badge>
                                )}
                            />
                            <KeyValueRow label="Result" value={<Badge>{state.selectedFixture.result}</Badge>} />
                        </>
                    ) : (
                        <div className="text-sm text-muted-foreground">Select a fixture to view details.</div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
